import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  TextField,
  Chip,
  Radio,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  InputAdornment,
  Tabs,
  Tab,
  Avatar
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import NearMeIcon from "@mui/icons-material/NearMe";
import BusinessIcon from "@mui/icons-material/Business";
import FactoryIcon from "@mui/icons-material/Factory";
import VerifiedIcon from "@mui/icons-material/Verified";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../request/Postrequest";
import {
  FETCH_SITE_TYPES,
  FETCH_COMPANIES_BY_NAME
} from "../../constant/Apipath";
import { BIBlue, BIGrey, lightenHexColor } from "../../assets/helpers";
import SearchMap from "../sites/SearchMap";
import Getcurrentlocation from "../common/Getcurrentlocation";
import { getAddress } from "../../request/Fetchcurrentaddress";
import { English } from "../../constant/EnglishFallback";

const PAGE_SIZE = 20;

const ProjectsFilterModal = ({
  open,
  onClose,
  initialFilters = {},
  onApplyFilters
}) => {
  const { multiLanguageData, loginData } = useSelector((state) => state?.main);
  const LanguageData = multiLanguageData?.Projects || {};

  const {
    TEXT_FILTERS = "Filters",
    BUTTON_APPLY = "Apply",
    BUTTON_CLEARALL = "Clear all",
    TEXT_ENTER_LOCATION = "Address / Location",
    TEXT_COMPANY = "Company",
    TEXT_SEARCH_COMPANIES = "Search companies...",
    TEXT_NO_COMPANIES_AVAILABLE = "No companies found",
    TEXT_PROJECT_TYPE = "Project Type",
    TEXT_ALL_PROJECT_TYPES = "All Types",
    BUTTON_SEARCH = "Search"
  } = LanguageData;

  // Active Tab: 0 = Project Type, 1 = Company
  const [activeTab, setActiveTab] = useState(0);

  // Draft filters (Draft & Commit pattern)
  const [draftProjectType, setDraftProjectType] = useState(
    initialFilters?.selectedProjectType || null
  );
  const [draftCompany, setDraftCompany] = useState(
    initialFilters?.selectedCompany || null
  );
  const [draftAddress, setDraftAddress] = useState(
    initialFilters?.address || ""
  );
  const [draftAddressDetails, setDraftAddressDetails] = useState(
    initialFilters?.addressDetails || null
  );

  // Project Types states
  const [projectTypes, setProjectTypes] = useState([]);
  const [projectTypesLoading, setProjectTypesLoading] = useState(false);
  const [hasMoreProjectTypes, setHasMoreProjectTypes] = useState(true);
  const [isLoadingMoreProjectTypes, setIsLoadingMoreProjectTypes] = useState(false);
  const projectTypesPageRef = useRef(1);

  // Company list states
  const [companies, setCompanies] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [isCompanySearching, setIsCompanySearching] = useState(false);
  const [hasMoreCompanies, setHasMoreCompanies] = useState(true);
  const [isLoadingMoreCompanies, setIsLoadingMoreCompanies] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const companyPageRef = useRef(1);
  const companySearchTimeout = useRef(null);
  const appliedCompanySearchRef = useRef("");

  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Synchronize draft states when modal opens
  useEffect(() => {
    if (open) {
      setDraftProjectType(initialFilters?.selectedProjectType || null);
      setDraftCompany(initialFilters?.selectedCompany || null);
      setDraftAddress(initialFilters?.address || "");
      setDraftAddressDetails(initialFilters?.addressDetails || null);
      setCompanySearch("");
      appliedCompanySearchRef.current = "";

      if (initialFilters?.selectedCompany && !initialFilters?.selectedProjectType) {
        setActiveTab(1);
      } else {
        setActiveTab(0);
      }

      projectTypesPageRef.current = 1;
      setHasMoreProjectTypes(true);
      fetchProjectTypes(1);

      companyPageRef.current = 1;
      setHasMoreCompanies(true);
      fetchCompanies(1, "");
    }
  }, [open, initialFilters]);

  // Fetch Project Types
  const fetchProjectTypes = useCallback(
    async (pageNumber = 1) => {
      try {
        const isFirstPage = pageNumber === 1;
        if (isFirstPage) {
          setProjectTypesLoading(true);
        } else {
          setIsLoadingMoreProjectTypes(true);
        }

        const payload = {
          PageNr: pageNumber,
          NrOfRecPerPage: PAGE_SIZE,
          FullSearch: "",
          IncludeRecordNr: "true",
          FetchAllowedRecordsOnly: true,
          SearchList: [{}],
          SortList: [{ FieldName: "Name", Direction: "ASC" }]
        };

        const res = await Postrequestcall(
          FETCH_SITE_TYPES,
          payload,
          loginData?.accessToken
        );

        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.dataList)
          ? res.dataList
          : [];

        const totalRecords = Number(
          res?.data?.nrOfRecords ??
          res?.data?.RecordNr ??
          res?.nrOfRecords ??
          res?.RecordNr ??
          list.length
        );

        const mapped = list.map((t) => ({
          id: t.id,
          value: t.id,
          name: t.name || t.label,
          label: t.name || t.label
        }));

        setProjectTypes((prev) => (isFirstPage ? mapped : [...prev, ...mapped]));
        const totalLoaded = isFirstPage ? mapped.length : (pageNumber - 1) * PAGE_SIZE + mapped.length;
        if (mapped.length < PAGE_SIZE || (totalRecords > 0 && totalLoaded >= totalRecords)) {
          setHasMoreProjectTypes(false);
        } else {
          projectTypesPageRef.current = pageNumber + 1;
        }
      } catch (err) {
        console.error("[ProjectsFilterModal] fetchProjectTypes error:", err);
        setHasMoreProjectTypes(false);
      } finally {
        setProjectTypesLoading(false);
        setIsLoadingMoreProjectTypes(false);
      }
    },
    [loginData?.accessToken]
  );

  const handleLoadMoreProjectTypes = () => {
    if (hasMoreProjectTypes && !projectTypesLoading && !isLoadingMoreProjectTypes) {
      fetchProjectTypes(projectTypesPageRef.current);
    }
  };

  // Fetch Companies
  const fetchCompanies = useCallback(
    async (pageNumber = 1, search = "", append = false, isUserSearch = false) => {
      const isFirstPage = pageNumber === 1 && !append;
      try {
        setCompanyLoading(true);
        if (isUserSearch) {
          setIsCompanySearching(true);
        }
        if (pageNumber > 1 || append) {
          setIsLoadingMoreCompanies(true);
        } else {
          setCompanies([]);
        }

        const payload = {
          PageNr: pageNumber,
          NrOfRecPerPage: PAGE_SIZE,
          FullSearch: search?.trim() || "",
          IncludeRecordNr: "true",
          TypeOfObjectReturned: "",
          FetchAllowedRecordsOnly: false,
          SearchList: [{}],
          SortList: [
            { FieldName: "Verified", Direction: "DESC" },
            { FieldName: "Name", Direction: "ASC" }
          ]
        };

        const res = await Postrequestcall(
          FETCH_COMPANIES_BY_NAME,
          payload,
          loginData?.accessToken
        );

        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.dataList)
          ? res.dataList
          : [];

        const totalRecords = Number(
          res?.data?.nrOfRecords ??
          res?.data?.RecordNr ??
          res?.nrOfRecords ??
          res?.RecordNr ??
          list.length
        );

        setCompanies((prev) => (isFirstPage ? list : [...prev, ...list]));
        const totalLoaded = isFirstPage ? list.length : (pageNumber - 1) * PAGE_SIZE + list.length;
        if (list.length < PAGE_SIZE || (totalRecords > 0 && totalLoaded >= totalRecords)) {
          setHasMoreCompanies(false);
        } else {
          companyPageRef.current = pageNumber + 1;
        }
      } catch (err) {
        console.error("[ProjectsFilterModal] fetchCompanies error:", err);
        setHasMoreCompanies(false);
      } finally {
        setCompanyLoading(false);
        setIsCompanySearching(false);
        setIsLoadingMoreCompanies(false);
      }
    },
    [loginData?.accessToken]
  );

  const handleLoadMoreCompanies = () => {
    if (hasMoreCompanies && !companyLoading && !isLoadingMoreCompanies) {
      fetchCompanies(companyPageRef.current, appliedCompanySearchRef.current, true, false);
    }
  };

  const handleCompanySearchChange = (e) => {
    const val = e.target.value;
    setCompanySearch(val);
    if (!val && appliedCompanySearchRef.current) {
      if (companySearchTimeout.current) clearTimeout(companySearchTimeout.current);
      appliedCompanySearchRef.current = "";
      companyPageRef.current = 1;
      setHasMoreCompanies(true);
      fetchCompanies(1, "", false, false);
    }
  };

  const handleSearchCompanies = (searchVal = companySearch) => {
    if (companySearchTimeout.current) clearTimeout(companySearchTimeout.current);
    companyPageRef.current = 1;
    setHasMoreCompanies(true);
    appliedCompanySearchRef.current = searchVal;
    fetchCompanies(1, searchVal, false, true);
  };

  const handleClearCompanySearch = () => {
    if (companySearchTimeout.current) clearTimeout(companySearchTimeout.current);
    setCompanySearch("");
    companyPageRef.current = 1;
    setHasMoreCompanies(true);
    appliedCompanySearchRef.current = "";
    fetchCompanies(1, "", false, false);
  };

  // Google Places place selection
  const handlePlaceSelect = (place) => {
    const lat = Number(
      typeof place?.geometry?.location?.lat === "function"
        ? place.geometry.location.lat()
        : place?.geometry?.location?.lat ?? 0
    );
    const lng = Number(
      typeof place?.geometry?.location?.lng === "function"
        ? place.geometry.location.lng()
        : place?.geometry?.location?.lng ?? 0
    );

    let address = "";
    let city = "";
    let province = "";
    let country = "";

    if (place?.address_components?.length > 0) {
      for (let i = 0; i < place.address_components.length; i++) {
        const comp = place.address_components[i];
        if (comp.types.includes("street_number")) address += comp.long_name + " ";
        if (comp.types.includes("route")) address += comp.long_name + " ";
        if (comp.types.includes("locality") || comp.types.includes("postal_town")) {
          city = comp.long_name;
        }
        if (comp.types.includes("administrative_area_level_1")) {
          province = comp.short_name;
        }
        if (comp.types.includes("country")) {
          country = comp.long_name;
        }
      }
    }

    const formattedAddress =
      place?.formatted_address ||
      (city ? `${city}${province ? `, ${province}` : ""}` : address.trim());

    setDraftAddress(formattedAddress);
    setDraftAddressDetails({
      city,
      province,
      country,
      latitude: lat,
      longitude: lng,
      formattedAddress
    });
  };

  // Clear Address
  const handleClearAddress = () => {
    setDraftAddress("");
    setDraftAddressDetails(null);
  };

  // GPS "Use Current Location"
  const handleCurrentLocationClick = async () => {
    try {
      setIsGettingLocation(true);
      const loc = await Getcurrentlocation(multiLanguageData);
      if (loc && !loc.default && loc.lat && loc.lng) {
        const addressData = await getAddress({ lat: loc.lat, lng: loc.lng });
        const {
          city = "",
          province = "",
          country = "",
          address = "",
          fullAddress = ""
        } = addressData?.objAddress || {};
        const resolvedAddress = city
          ? `${city}${province ? `, ${province}` : ""}`
          : fullAddress || address || `${loc.lat}, ${loc.lng}`;

        setDraftAddress(resolvedAddress);
        setDraftAddressDetails({
          city,
          province,
          country,
          latitude: loc.lat,
          longitude: loc.lng,
          formattedAddress: resolvedAddress
        });
      }
    } catch (err) {
      console.error("handleCurrentLocationClick error:", err);
    } finally {
      setIsGettingLocation(false);
    }
  };

  // Active filter count in draft state
  const draftFilterCount =
    (draftProjectType ? 1 : 0) +
    (draftCompany ? 1 : 0) +
    (draftAddress?.trim() ? 1 : 0);

  // Clear all draft filters
  const handleClearAll = () => {
    if (companySearchTimeout.current) clearTimeout(companySearchTimeout.current);
    setDraftProjectType(null);
    setDraftCompany(null);
    setDraftAddress("");
    setDraftAddressDetails(null);
    setCompanySearch("");
    if (appliedCompanySearchRef.current !== "") {
      appliedCompanySearchRef.current = "";
      companyPageRef.current = 1;
      setHasMoreCompanies(true);
      fetchCompanies(1, "");
    }
  };

  // Apply filters
  const handleApply = () => {
    if (typeof onApplyFilters === "function") {
      onApplyFilters({
        selectedProjectType: draftProjectType,
        selectedCompany: draftCompany,
        address: draftAddress,
        addressDetails: draftAddressDetails
      });
    }
    onClose();
  };

  const handleScrollTypes = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 30) {
      handleLoadMoreProjectTypes();
    }
  };

  const handleScrollCompanies = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 30) {
      handleLoadMoreCompanies();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)"
          }
        }
      }}
    >
      <Box
        className="user-modal-responsive"
        sx={{
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          p: { xs: 2.5, sm: 3 },
          maxWidth: "520px !important",
          borderRadius: "20px !important",
          boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)",
          bgcolor: "#ffffff"
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 2,
            borderBottom: "1px solid #f1f5f9"
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                bgcolor: lightenHexColor(BIBlue, 92),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: BIBlue
              }}
            >
              <FilterListIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f172a" }}>
              {TEXT_FILTERS}
            </Typography>
            {draftFilterCount > 0 && (
              <Chip
                label={`${draftFilterCount} active`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  bgcolor: lightenHexColor(BIBlue, 92),
                  color: BIBlue,
                  borderRadius: "6px"
                }}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {draftFilterCount > 0 && (
              <Button
                variant="text"
                size="small"
                onClick={handleClearAll}
                sx={{
                  textTransform: "none",
                  color: "#64748b",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1,
                  "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" }
                }}
              >
                {BUTTON_CLEARALL}
              </Button>
            )}
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                width: 32,
                height: 32,
                bgcolor: "#f8fafc",
                color: "#64748b",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                "&:hover": { bgcolor: "#f1f5f9", color: "#1e293b" }
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Box>

        {/* Scrollable Body */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            py: 2,
            pr: 0.5,
            "&::-webkit-scrollbar": { width: 5 },
            "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 4 }
          }}
        >
          {/* Section 1: Address / Location */}
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: "14px",
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0"
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.25 }}>
              <LocationOnIcon sx={{ fontSize: 17, color: BIBlue }} />
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "#475569"
                }}
              >
                {TEXT_ENTER_LOCATION || "Location"}
              </Typography>
            </Stack>

            <SearchMap
              searchTerm={draftAddress}
              setSearchTerm={setDraftAddress}
              handleSearch={handlePlaceSelect}
              onClear={handleClearAddress}
              useDetailedLabel
            />

            <Button
              startIcon={
                isGettingLocation ? (
                  <CircularProgress size={14} sx={{ color: BIBlue }} />
                ) : (
                  <NearMeIcon sx={{ fontSize: 15 }} />
                )
              }
              variant="text"
              size="small"
              disabled={isGettingLocation}
              onClick={handleCurrentLocationClick}
              sx={{
                mt: 1,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.78rem",
                color: BIBlue,
                borderRadius: "8px",
                px: 1,
                py: 0.25,
                "&:hover": { bgcolor: lightenHexColor(BIBlue, 94) }
              }}
            >
              {isGettingLocation
                ? "Detecting location..."
                : "Use current location"}
            </Button>
          </Box>

          {/* Section 2: Segmented Switch for Project Type & Company */}
          <Box sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                p: 0.5,
                bgcolor: "#f1f5f9",
                borderRadius: "12px",
                border: "1px solid #e2e8f0"
              }}
            >
              <Box
                onClick={() => setActiveTab(0)}
                sx={{
                  flex: 1,
                  py: 1,
                  borderRadius: "9px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  cursor: "pointer",
                  fontWeight: activeTab === 0 ? 700 : 600,
                  fontSize: "0.83rem",
                  bgcolor: activeTab === 0 ? "#ffffff" : "transparent",
                  color: activeTab === 0 ? BIBlue : "#64748b",
                  boxShadow: activeTab === 0 ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
                  transition: "all 0.18s ease"
                }}
              >
                <CategoryIcon sx={{ fontSize: 17 }} />
                <span>{TEXT_PROJECT_TYPE}</span>
                {draftProjectType && (
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: BIBlue
                    }}
                  />
                )}
              </Box>

              <Box
                onClick={() => setActiveTab(1)}
                sx={{
                  flex: 1,
                  py: 1,
                  borderRadius: "9px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  cursor: "pointer",
                  fontWeight: activeTab === 1 ? 700 : 600,
                  fontSize: "0.83rem",
                  bgcolor: activeTab === 1 ? "#ffffff" : "transparent",
                  color: activeTab === 1 ? BIBlue : "#64748b",
                  boxShadow: activeTab === 1 ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
                  transition: "all 0.18s ease"
                }}
              >
                <BusinessIcon sx={{ fontSize: 17 }} />
                <span>{TEXT_COMPANY}</span>
                {draftCompany && (
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: BIBlue
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Tab 0: Project Type */}
          {activeTab === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
              {draftProjectType && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    width: "100%",
                    px: 1.5,
                    py: 0.75,
                    mb: 1.25,
                    borderRadius: "10px",
                    bgcolor: lightenHexColor(BIBlue, 94),
                    border: "1px solid",
                    borderColor: lightenHexColor(BIBlue, 85)
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                    <CategoryIcon sx={{ fontSize: 16, color: BIBlue, flexShrink: 0 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        color: BIBlue,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      Selected: {draftProjectType.name || draftProjectType.label}
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    onClick={() => setDraftProjectType(null)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      p: 0,
                      minWidth: "auto",
                      color: "#ef4444",
                      ml: "auto",
                      alignSelf: "center",
                      flexShrink: 0,
                      justifyContent: "flex-end",
                      "&:hover": { bgcolor: "transparent", color: "#dc2626" }
                    }}
                  >
                    Clear
                  </Button>
                </Stack>
              )}

              <Box
                onScroll={handleScrollTypes}
                sx={{
                  flex: 1,
                  maxHeight: 250,
                  overflowY: "auto",
                  border: "1px solid #f1f5f9",
                  borderRadius: "12px",
                  p: 0.5,
                  "&::-webkit-scrollbar": { width: 5 },
                  "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 4 }
                }}
              >
                <List disablePadding>
                  {/* All Types option */}
                  <ListItem disablePadding>
                    <ListItemButton
                      dense
                      onClick={() => setDraftProjectType(null)}
                      sx={{
                        py: 0.65,
                        px: 1.25,
                        borderRadius: "8px",
                        my: 0.25,
                        bgcolor: draftProjectType === null ? lightenHexColor(BIBlue, 94) : "transparent",
                        "&:hover": {
                          bgcolor: draftProjectType === null
                            ? lightenHexColor(BIBlue, 90)
                            : "#f8fafc"
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Radio
                          edge="start"
                          checked={draftProjectType === null}
                          size="small"
                          disableRipple
                          sx={{
                            // p: 0.25,
                            color: draftProjectType === null ? BIBlue : undefined,
                            "&.Mui-checked": { color: BIBlue }
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={TEXT_ALL_PROJECT_TYPES}
                        primaryTypographyProps={{
                          fontSize: "0.84rem",
                          fontWeight: draftProjectType === null ? 700 : 500,
                          color: draftProjectType === null ? BIBlue : "#334155"
                        }}
                      />
                    </ListItemButton>
                  </ListItem>

                  {projectTypes.map((type) => {
                    const isSelected = draftProjectType?.id === type.id;
                    return (
                      <ListItem key={type.id} disablePadding>
                        <ListItemButton
                          dense
                          onClick={() =>
                            setDraftProjectType(isSelected ? null : type)
                          }
                          sx={{
                            py: 0.65,
                            px: 1.25,
                            borderRadius: "8px",
                            my: 0.25,
                            bgcolor: isSelected ? lightenHexColor(BIBlue, 94) : "transparent",
                            "&:hover": {
                              bgcolor: isSelected
                                ? lightenHexColor(BIBlue, 90)
                                : "#f8fafc"
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Radio
                              edge="start"
                              checked={isSelected}
                              size="small"
                              disableRipple
                              sx={{
                                // p: 0.25,
                                color: isSelected ? BIBlue : undefined,
                                "&.Mui-checked": { color: BIBlue }
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={type.name || type.label}
                            primaryTypographyProps={{
                              fontSize: "0.84rem",
                              fontWeight: isSelected ? 700 : 500,
                              color: isSelected ? BIBlue : "#334155"
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}

                  {isLoadingMoreProjectTypes && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                      <CircularProgress size={18} sx={{ color: BIBlue }} />
                    </Box>
                  )}
                </List>

                {projectTypesLoading && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} sx={{ color: BIBlue }} />
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Tab 1: Company */}
          {activeTab === 1 && (
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
              {/* Company search row with search bar and button */}
              <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={TEXT_SEARCH_COMPANIES}
                  value={companySearch}
                  onChange={handleCompanySearchChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearchCompanies();
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: BIGrey, fontSize: 19 }} />
                      </InputAdornment>
                    ),
                    endAdornment: companySearch ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleClearCompanySearch}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      bgcolor: "#f8fafc"
                    }
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleSearchCompanies(companySearch)}
                  disabled={companyLoading}
                  sx={{
                    minWidth: 86,
                    width: 86,
                    height: 40,
                    p: 0,
                    borderRadius: "10px",
                    bgcolor: BIBlue,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    whiteSpace: "nowrap",
                    boxShadow: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      bgcolor: BIBlue,
                      boxShadow: "none"
                    },
                    "&.Mui-disabled": {
                      bgcolor: isCompanySearching ? lightenHexColor(BIBlue, 40) : BIBlue,
                      color: "#ffffff"
                    }
                  }}
                >
                  {isCompanySearching ? (
                    <CircularProgress size={18} sx={{ color: "#ffffff" }} />
                  ) : (
                    BUTTON_SEARCH
                  )}
                </Button>
              </Stack>

              {draftCompany && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    width: "100%",
                    px: 1.5,
                    py: 0.75,
                    mb: 1.25,
                    borderRadius: "10px",
                    bgcolor: lightenHexColor(BIBlue, 94),
                    border: "1px solid",
                    borderColor: lightenHexColor(BIBlue, 85)
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                    <BusinessIcon sx={{ fontSize: 16, color: BIBlue, flexShrink: 0 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        color: BIBlue,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      Selected: {(draftCompany.name || draftCompany.companyName || "").trim()}
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    onClick={() => setDraftCompany(null)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      p: 0,
                      minWidth: "auto",
                      color: "#ef4444",
                      ml: "auto",
                      alignSelf: "center",
                      flexShrink: 0,
                      justifyContent: "flex-end",
                      "&:hover": { bgcolor: "transparent", color: "#dc2626" }
                    }}
                  >
                    Clear
                  </Button>
                </Stack>
              )}

              <Box
                onScroll={handleScrollCompanies}
                sx={{
                  flex: 1,
                  maxHeight: 230,
                  overflowY: "auto",
                  border: "1px solid #f1f5f9",
                  borderRadius: "12px",
                  p: 0.5,
                  "&::-webkit-scrollbar": { width: 5 },
                  "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 4 }
                }}
              >
                <List disablePadding>
                  {companies.map((comp) => {
                    const isSelected =
                      draftCompany?.id === comp.id ||
                      draftCompany?.value === comp.id;
                    const logo = comp.logo || comp.companyLogo;
                    const loc = [comp.city, comp.province].filter(Boolean).join(", ");
                    const name = (comp.name || comp.companyName || "").trim();

                    return (
                      <ListItem key={comp.id || comp.value} disablePadding>
                        <ListItemButton
                          dense
                          onClick={() =>
                            setDraftCompany(isSelected ? null : comp)
                          }
                          sx={{
                            py: 0.65,
                            px: 1.25,
                            borderRadius: "8px",
                            my: 0.25,
                            bgcolor: isSelected
                              ? lightenHexColor(BIBlue, 94)
                              : "transparent",
                            "&:hover": {
                              bgcolor: isSelected
                                ? lightenHexColor(BIBlue, 90)
                                : "#f8fafc"
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Radio
                              edge="start"
                              checked={isSelected}
                              size="small"
                              disableRipple
                              sx={{
                                // p: 0.25,
                                color: isSelected ? BIBlue : undefined,
                                "&.Mui-checked": { color: BIBlue }
                              }}
                            />
                          </ListItemIcon>

                          <Avatar
                            src={logo}
                            variant="rounded"
                            sx={{
                              width: 28,
                              height: 28,
                              mr: 1.25,
                              bgcolor: lightenHexColor(BIBlue, 92),
                              borderRadius: "6px"
                            }}
                          >
                            {comp.manufacturer ? (
                              <FactoryIcon sx={{ fontSize: 16, color: BIBlue }} />
                            ) : (
                              <BusinessIcon sx={{ fontSize: 16, color: BIBlue }} />
                            )}
                          </Avatar>

                          <ListItemText
                            primary={
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography
                                  variant="body2"
                                  fontWeight={isSelected ? 700 : 500}
                                  sx={{
                                    fontSize: "0.84rem",
                                    color: isSelected ? BIBlue : "#1e293b",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                  }}
                                >
                                  {name}
                                </Typography>
                                {comp.verified && (
                                  <VerifiedIcon
                                    sx={{ fontSize: 15, color: BIBlue }}
                                  />
                                )}
                              </Stack>
                            }
                            secondary={
                              loc ? (
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#64748b", fontSize: "0.75rem" }}
                                  noWrap
                                >
                                  {loc}
                                </Typography>
                              ) : null
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}

                  {isLoadingMoreCompanies && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                      <CircularProgress size={18} sx={{ color: BIBlue }} />
                    </Box>
                  )}

                  {!companyLoading && companies.length > 0 && hasMoreCompanies && (
                    <Box sx={{ p: 1, textAlign: "center" }}>
                      <Button
                        size="small"
                        onClick={handleLoadMoreCompanies}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: BIBlue
                        }}
                      >
                        Load more companies
                      </Button>
                    </Box>
                  )}

                  {!companyLoading && companies.length === 0 && (
                    <Box sx={{ textAlign: "center", py: 3 }}>
                      <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.82rem" }}>
                        {TEXT_NO_COMPANIES_AVAILABLE}
                      </Typography>
                    </Box>
                  )}
                </List>

                {companyLoading && companies.length === 0 && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} sx={{ color: BIBlue }} />
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            pt: 2,
            mt: 0.5,
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Button
            variant="text"
            onClick={handleClearAll}
            disabled={draftFilterCount === 0}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.84rem",
              color: draftFilterCount > 0 ? "#64748b" : "#cbd5e1",
              borderRadius: "10px",
              px: 1.5,
              "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" }
            }}
          >
            {BUTTON_CLEARALL}
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.88rem",
              px: 3.5,
              py: 1,
              borderRadius: "12px",
              bgcolor: BIBlue,
              boxShadow: "0 6px 16px rgba(8, 38, 184, 0.28)",
              "&:hover": {
                bgcolor: BIBlue,
                boxShadow: "0 8px 20px rgba(8, 38, 184, 0.35)"
              }
            }}
          >
            {draftFilterCount > 0 ? `${BUTTON_APPLY} (${draftFilterCount})` : BUTTON_APPLY}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ProjectsFilterModal;
